import React, { useCallback, useEffect, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import {
  UserCircleIcon, BuildingOffice2Icon, KeyIcon, RectangleStackIcon,
  TrashIcon, CheckCircleIcon, ExclamationTriangleIcon, HeartIcon,
  DocumentTextIcon, BanknotesIcon, WrenchScrewdriverIcon, UserGroupIcon, BellIcon, PlusCircleIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../context/AuthContext'
import Loader from '../components/Loader'
import useDocumentTitle from '../hooks/useDocumentTitle'
import * as api from '../api/account'
import ProfileSection from '../components/account/ProfileSection'
import CompanySection from '../components/account/CompanySection'
import PasswordSection from '../components/account/PasswordSection'
import SavedKitsSection from '../components/account/SavedKitsSection'
import ObservedSection from '../components/account/ObservedSection'
import DangerSection from '../components/account/DangerSection'
import { ProjektSection, MarzeSection, MontazSection, UslugiSection, WygladFirmySection } from '../components/account/WycenaSections'
import ZespolSection from '../components/account/ZespolSection'
import PowiadomieniaSection from '../components/account/PowiadomieniaSection'
import PlanKonta from '../components/plany/PlanKonta'

// Preferencji doboru TU NIE MA — mają własną stronę `/preferencje` i pierwszą
// pozycję w menu konta (UserMenu). Były najgłębiej schowaną zakładką, a zmienia
// się je najczęściej ze wszystkiego, co jest na koncie.
const SEKCJE = [
  { key: 'profil', label: 'Dane i typ działalności', Icon: UserCircleIcon },
  { key: 'firma', label: 'Firma', Icon: BuildingOffice2Icon },
  // Zespół firmy (konta zespołowe, 2026-10-11): dla konta firmowego; bez podpiętej firmy zakładka podpowiada,
  // że trzeba ją dodać (wcześniej znikała i nie było wiadomo, czemu).
  { key: 'zespol', label: 'Zespół', Icon: UserGroupIcon, firmowe: true },
  // Trzy zakładki TYLKO dla konta firmowego. Osobie prywatnej marże i stawki
  // montażu nie mówią nic poza tym, że trafiła nie tam, gdzie trzeba.
  { key: 'projekt', label: 'Oferta i Projekt', Icon: DocumentTextIcon, firmowe: true },
  { key: 'marze', label: 'Marże', Icon: BanknotesIcon, firmowe: true },
  { key: 'montaz', label: 'Stawki montażu', Icon: WrenchScrewdriverIcon, firmowe: true },
  // Usługi dodatkowe firmy (runda 6, 2026-10-12): doliczane do ceny instalacji przy wycenie klienta.
  { key: 'uslugi', label: 'Usługi dodatkowe', Icon: PlusCircleIcon, firmowe: true },
  { key: 'powiadomienia', label: 'Powiadomienia', Icon: BellIcon, firmowe: true },
  { key: 'haslo', label: 'Hasło', Icon: KeyIcon },
  { key: 'zestawy', label: 'Zapisane zestawy', Icon: RectangleStackIcon },
  { key: 'obserwowane', label: 'Obserwowane', Icon: HeartIcon },
  { key: 'usuniecie', label: 'Usunięcie konta', Icon: TrashIcon },
]

/** Krótki komunikat po zapisie — znika sam, nie wymaga zamykania. */
export const useFlash = () => {
  const [flash, setFlash] = useState(null)
  useEffect(() => {
    if (!flash) return undefined
    const t = setTimeout(() => setFlash(null), 4000)
    return () => clearTimeout(t)
  }, [flash])
  return [flash, setFlash]
}

const Account = () => {
  useDocumentTitle('Moje konto')
  const { isAuthenticated, loading: authLoading } = useAuth()

  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // Wejscie wprost w zakladke z menu konta (/konto?sekcja=projekt). Bez tego
  // pozycja „Oferta i Projekt" w menu wrzucalaby na profil i kazala szukac.
  const [parametry] = useSearchParams()
  const [sekcja, setSekcja] = useState(parametry.get('sekcja') || 'profil')
  // Kliknięcie pozycji menu (np. „Zespół”), stojąc już na /konto, zmienia tylko adres: komponent
  // zostaje, więc zakładkę przestawiamy za adresem.
  const sekcjaZAdresu = parametry.get('sekcja')
  useEffect(() => { if (sekcjaZAdresu) setSekcja(sekcjaZAdresu) }, [sekcjaZAdresu])
  const [flash, setFlash] = useFlash()

  const load = useCallback(async () => {
    const res = await api.getProfile()
    if (res.ok) setProfile(res.data)
    else setError(res.error)
    setLoading(false)
  }, [])

  useEffect(() => { if (isAuthenticated) load() }, [isAuthenticated, load])

  // Dopóki nie wiemy, czy ktoś jest zalogowany, NIE przekierowujemy — inaczej
  // odświeżenie strony konta wyrzucałoby zalogowanego na logowanie.
  if (authLoading) {
    return <div className="flex justify-center py-24"><Loader /></div>
  }
  if (!isAuthenticated) {
    return <Navigate to="/logowanie" state={{ from: '/konto' }} replace />
  }
  if (loading) {
    return <div className="flex justify-center py-24"><Loader /></div>
  }

  const wspolne = { profile, setProfile, setFlash }

  // Zakładki wyceny tylko dla konta firmowego. Gdyby ktoś przełączył konto na
  // prywatne, stojąc na jednej z nich, zostałby przy pustym widoku — dlatego
  // `aktywna` cofa się wtedy na profil.
  const firmowe = profile?.account_type === 'company'
  const widoczneSekcje = SEKCJE.filter((s) => (!s.firmowe || firmowe) && (!s.zFirma || profile?.company))
  const aktywna = widoczneSekcje.some((s) => s.key === sekcja) ? sekcja : 'profil'

  return (
    // `max-w-6xl`, nie 5xl: sekcja preferencji mieści trzy kafelki modułów
    // w rzędzie — przy węższym kontenerze łamały się na 2+1.
    <div className="px-4 md:px-8 lg:px-12 py-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Moje konto</h1>
        <p className="mt-1 text-sm text-gray-500">{profile?.email}</p>
        <PlanKonta />
      </div>

      {flash && (
        <div className="mb-5 flex items-center gap-2.5 rounded-lg bg-emerald-50 border border-emerald-100 p-3">
          <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <p className="text-sm text-emerald-800">{flash}</p>
        </div>
      )}
      {error && (
        <div className="mb-5 flex items-center gap-2.5 rounded-lg bg-red-50 border border-red-100 p-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!profile?.email_verified && (
        <div className="mb-5 rounded-lg bg-amber-50 border border-amber-100 p-3">
          <p className="text-sm text-amber-800">
            Twój adres e-mail nie został jeszcze potwierdzony. Sprawdź skrzynkę, także folder spam.
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-[220px_1fr] gap-6 items-start">
        {/* Nawigacja sekcji. Na wąskim ekranie zwija się do poziomego paska. */}
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible
                        bg-white rounded-2xl border border-gray-100 shadow-sm p-2">
          {widoczneSekcje.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setSekcja(key)}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-left
                          whitespace-nowrap transition-colors ${
                aktywna === key
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-600 hover:bg-gray-50'
              } ${key === 'usuniecie' ? 'lg:mt-2 lg:border-t lg:border-gray-100 lg:pt-3' : ''}`}
            >
              <Icon className={`w-5 h-5 flex-shrink-0 ${key === 'usuniecie' ? 'text-red-400' : ''}`} />
              {label}
            </button>
          ))}
        </nav>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
          {aktywna === 'profil' && <ProfileSection {...wspolne} />}
          {aktywna === 'firma' && (
            <>
              <CompanySection {...wspolne} />
              {/* Logo i kolor firmy (decyzja 2026-10-11): ustawienia firmowe, więc tylko dla konta firmowego. */}
              {firmowe && <WygladFirmySection {...wspolne} />}
            </>
          )}
          {aktywna === 'zespol' && <ZespolSection {...wspolne} />}
          {aktywna === 'projekt' && <ProjektSection {...wspolne} />}
          {aktywna === 'marze' && <MarzeSection {...wspolne} />}
          {aktywna === 'montaz' && <MontazSection {...wspolne} />}
          {aktywna === 'uslugi' && <UslugiSection {...wspolne} />}
          {aktywna === 'powiadomienia' && <PowiadomieniaSection {...wspolne} />}
          {aktywna === 'haslo' && <PasswordSection {...wspolne} />}
          {aktywna === 'zestawy' && <SavedKitsSection {...wspolne} />}
          {aktywna === 'obserwowane' && <ObservedSection />}
          {aktywna === 'usuniecie' && <DangerSection {...wspolne} />}
        </div>
      </div>
    </div>
  )
}

export default Account
